import jsPDF from 'jspdf';

import { JPEG_PDF_QUALITY, PDF_MARGIN_PT } from 'components/Documents/Scanner/constants';

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read image blob'));
    reader.readAsDataURL(blob);
  });

/**
 * Builds a multi-page A4 PDF from JPEG blobs while preserving aspect ratio.
 */
export const generatePdfFromImageBlobs = async (imageBlobs: Blob[]): Promise<Blob> => {
  if (imageBlobs.length === 0) {
    throw new Error('No images provided for PDF generation');
  }

  const pdf = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const maxWidth = pageWidth - PDF_MARGIN_PT * 2;
  const maxHeight = pageHeight - PDF_MARGIN_PT * 2;

  for (let index = 0; index < imageBlobs.length; index += 1) {
    if (index > 0) {
      pdf.addPage();
    }

    const compressedBlob = await recompressBlob(imageBlobs[index], JPEG_PDF_QUALITY);
    const dataUrl = await blobToDataUrl(compressedBlob);

    const dimensions = await getImageDimensions(dataUrl);
    const scale = Math.min(maxWidth / dimensions.width, maxHeight / dimensions.height);
    const imgWidth = dimensions.width * scale;
    const imgHeight = dimensions.height * scale;
    const x = (pageWidth - imgWidth) / 2;
    const y = (pageHeight - imgHeight) / 2;

    pdf.addImage(dataUrl, 'JPEG', x, y, imgWidth, imgHeight);
  }

  return pdf.output('blob');
};

export interface ImageDocumentUploadPayload {
  generatedPdf: Blob;
  images: Blob[];
}

/**
 * Shared image-to-upload pipeline used by scanner and file-upload workflows.
 */
export const prepareImageDocumentUpload = async (
  imageBlobs: Blob[]
): Promise<ImageDocumentUploadPayload> => {
  const generatedPdf = await generatePdfFromImageBlobs(imageBlobs);
  return { generatedPdf, images: imageBlobs };
};

const getImageDimensions = (dataUrl: string): Promise<{ width: number; height: number }> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.width, height: image.height });
    image.onerror = () => reject(new Error('Failed to load image for PDF sizing'));
    image.src = dataUrl;
  });

const recompressBlob = async (blob: Blob, quality: number): Promise<Blob> => {
  const bitmap = await createImageBitmap(blob);
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;

  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new Error('Canvas context unavailable');
  }

  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  const result = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (output) => {
        if (output) {
          resolve(output);
        } else {
          reject(new Error('Image compression failed'));
        }
      },
      'image/jpeg',
      quality
    );
  });

  canvas.width = 0;
  canvas.height = 0;

  return result;
};
